import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponseWith } from './pagination.js';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Follow-ups (PART 10, CRM-03) — the ONE shared task engine: "ONE generic
 * Task entity with a polymorphic relatedType/relatedId reference, reusable
 * across modules (Leads, Opportunities, Customers, Projects…). NOT a
 * CRM-specific model."
 *
 * Permissions — where the closed 12x5 vocabulary meets rung 4:
 *   PART 16.1's rung-4 floor gives EVERY signed-in user — role or no role —
 *   "their own tasks". So the task routes are @SelfService('own-tasks') and
 *   the SERVICE holds the ladder:
 *     - reads are scoped to the caller's own tasks unless the caller holds
 *       crm.view (CRM-03 §9's task-visibility permission), which widens the
 *       Team/Calendar/Overdue population to everyone's;
 *     - anybody may create a task FOR THEMSELVES; assigning it to somebody
 *       else needs crm.create;
 *     - the assignee completes / reopens / cancels their own task; acting on
 *       somebody else's needs crm.edit.
 *   The list response NAMES the scope it answered with ('mine' | 'all') so
 *   the Overdue tab can label its own badge — BR-CRM-03-13's "a count that
 *   is not labelled with its population will eventually be read against the
 *   wrong one".
 *
 * Owner ruling R1 (BR-CRM-03-02): priority, recurrence and reminder are
 * always-visible inputs whose DEFAULTS THE SERVER SUPPLIES (normal / does
 * not repeat / off / 15 min) when the request leaves them out. No default is
 * inferred from anything the client claims about itself.
 *
 * Recurrence (BR-CRM-03-07/08): generate-on-completion — completing a
 * repeating task creates exactly ONE next copy (+1 day/week/month), notes
 * and completion time cleared; nothing is pre-created, so an untouched
 * repeating task never builds a backlog; reopen + re-complete never
 * double-spawns (Q-CRM-03-3).
 */
// ── Vocabularies (CRM-03 §5) ────────────────────────────────────────────────
export const TASK_KINDS = ['call', 'email', 'meeting', 'todo', 'demo'];
/** 'candidate' joined with HR-05 — interviews are tasks (BR-HR-05-08). */
export const TASK_RELATED_KINDS = ['lead', 'opportunity', 'customer', 'project', 'candidate'];
export const TASK_PRIORITIES = ['low', 'normal', 'high'];
export const TASK_RECURRENCES = ['none', 'daily', 'weekly', 'monthly'];
/** Stored statuses only — Overdue is computed, never stored (BR-CRM-03-03). */
export const TASK_STATUSES = ['pending', 'completed', 'cancelled'];
/** BR-CRM-03-02's server defaults, named once. */
export const TASK_DEFAULTS = {
    priority: 'normal',
    recurrence: 'none',
    reminder: false,
    reminderMinutes: 15,
};
// ── The fixed sentences of CRM-03 ───────────────────────────────────────────
export const TASK_MESSAGES = {
    /** BR-CRM-03-01 / ERR-CRM-03-01 — title and due date-time, refused server-side too. */
    incomplete: 'A task needs a title and a due date and time.',
    /** BR-CRM-03-05 / ERR-CRM-03-03: the tick is inert on a cancelled task. */
    cancelledInert: 'This task is cancelled. Create a new task instead.',
    /** Completing / reopening / cancelling somebody else's task without crm.edit. */
    notYourTask: 'This task is assigned to somebody else. Ask your administrator.',
    /** Cancelling an already-finished task. */
    notPending: 'Only a pending task can be cancelled.',
    /**
     * The tick is a three-way toggle — completing twice is a reopen's job.
     * ERR-CRM-03-04 is somebody ticking quickly twice and finding the task
     * REOPENED; the toggle is deliberate, and this sentence is what says so.
     */
    alreadyCompleted: 'This task is already completed. Tick it again to reopen it.',
    /** Reopening something that was never completed. */
    notCompleted: 'Only a completed task can be reopened.',
    /** BR-CRM-03-10's title prefix. */
    followUpPrefix: 'Follow-up: ',
    /**
     * ── BR-DATA-01, AND THE §15 TENSION IT HAS TO LIVE WITH ──────────────────
     *
     * BR-DATA-01: "wherever a flexible pointer is used, the system must check
     * that the target record exists before saving, BECAUSE THE DATABASE
     * CANNOT." §17.2.4 lists the task's `related_type` + `related_id` as one of
     * the three, and names the cost in the same row: "the database cannot check
     * the link. The system must."
     *
     * CRM-03 §15 pulls the other way, and it is not a contradiction once the
     * two are read as being about different moments:
     *
     *   §15 "Deleted linked record. The related record is stored as free text
     *        and a derived key, so THE TASK SURVIVES even if the record it
     *        names disappears. Nothing dangles, and nothing breaks."
     *
     * §15 is about DELETE TIME and BR-DATA-01 is about SAVE TIME. What makes
     * §15 safe is precisely that the task keeps its own copy of what it was
     * about — `relatedKey` and `relatedName` — so it can still be read after
     * the record is gone. That snapshot is a fallback for a pointer that WAS
     * once good; it is not a licence to store a pointer that never was. A
     * relatedId naming nothing has no snapshot to fall back to and no moment at
     * which it was true: it is a typo, or another tenant's identifier, and
     * writing it is the defect BR-DATA-01 names.
     *
     * So: checked on the way in, never cascaded on the way out. BR-DATA-02's
     * "deleting a record that a flexible pointer points at must not leave a
     * broken pointer" is answered for the task by the snapshot §15 relies on,
     * which is why no delete-time rule appears anywhere in this file.
     */
    relatedRecordMissing: (kind) => `That ${kind} no longer exists. Pick another one, or type the name instead.`,
    /**
     * A `relatedId` with no `relatedKind` cannot be checked by anybody — there
     * is no table to look in — so it is exactly the unverifiable pointer
     * BR-DATA-01 exists to refuse. ERR-CRM-03-02 covers the opposite shape (a
     * typed NAME with no kind) and deliberately allows it: a name is free text
     * and needs no target.
     */
    relatedKindRequired: 'Choose what kind of record this task is about.',
};
/**
 * D46 — CRM's related-record refusal, structured. `kind` is a display label
 * (the related record's type name). Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const TASK_REFUSALS = {
    relatedRecordMissing: (kind) => ({
        message: TASK_MESSAGES.relatedRecordMissing(kind),
        key: 'crm.errRelatedRecordMissing',
        params: { kind },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const TaskKindSchema = z.enum(TASK_KINDS);
export const TaskRelatedKindSchema = z.enum(TASK_RELATED_KINDS);
export const TaskPrioritySchema = z.enum(TASK_PRIORITIES);
export const TaskRecurrenceSchema = z.enum(TASK_RECURRENCES);
export const TaskStatusSchema = z.enum(TASK_STATUSES);
export const TaskSchema = z
    .object({
    id: z.uuid(),
    title: z.string(),
    kind: TaskKindSchema,
    relatedKind: TaskRelatedKindSchema.nullable(),
    relatedId: z.uuid().nullable(),
    relatedKey: z.string().nullable(),
    relatedName: z.string().nullable(),
    /** ISO date-time — both a day and a clock time (field 7). */
    dueAt: z.string(),
    assigneeId: z.uuid(),
    assigneeName: z.string().nullable(),
    priority: TaskPrioritySchema,
    recurrence: TaskRecurrenceSchema,
    reminder: z.boolean(),
    reminderMinutes: z.number().int().min(0),
    status: TaskStatusSchema,
    /** BR-CRM-03-04: pending AND due before now — computed, never stored. */
    overdue: z.boolean(),
    notes: z.string().nullable(),
    completedAt: z.string().nullable(),
    /** The task this one was spawned from (recurrence or Add follow-up). */
    sourceTaskId: z.uuid().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateTaskRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    dueAt: z.iso.datetime({ offset: true }),
    kind: TaskKindSchema.optional(),
    relatedKind: TaskRelatedKindSchema.optional(),
    relatedId: z.uuid().optional(),
    relatedKey: z.string().trim().max(30).optional(),
    relatedName: z.string().trim().max(160).optional(),
    /** Omitted = the caller themselves; somebody else needs crm.create. */
    assigneeId: z.uuid().optional(),
    /** BR-CRM-03-02: omitted fields take the SERVER defaults. */
    priority: TaskPrioritySchema.optional(),
    recurrence: TaskRecurrenceSchema.optional(),
    reminder: z.boolean().optional(),
    reminderMinutes: z.number().int().min(0).max(10080).optional(),
    notes: z.string().trim().max(2000).optional(),
})
    .strict();
export const CompleteTaskRequestSchema = z
    .object({
    /** The completion prompt's outcome text — genuinely optional (BR-CRM-03-09). */
    outcome: z.string().trim().max(2000).optional(),
})
    .strict();
/**
 * ── CRM-03's FIGURES ABOUT EVERY TASK, AND ITS FOUR BUCKETS ────────────────
 *
 * The follow-ups screen printed two counts and FOUR GROUPS from the whole
 * list: the open badge, the overdue badge, and Overdue / Today / This week /
 * Later, each a `.filter()` over every task the route returned.
 *
 * `PAGINATION.md` §3 step 10 covers the counts; the GROUPS are the harder
 * half, because each one is a list AND a heading with a number on it. Both
 * halves are answered here: the counts come from this route, and each group's
 * ROWS come from the paged list with `dueFrom`/`dueTo` — the same window this
 * counted, so a heading can never disagree with the block under it.
 *
 * The windows are the SERVER's, computed on the company's clock. The browser
 * used `new Date()` and `setHours(0,0,0,0)`, which buckets on the READER's
 * machine: a manager in another timezone saw a different "today" from the
 * company's, and BR-CRM-03-04's "strictly before now" was already the
 * server's. One clock decides both, or a task can be counted in Today and
 * listed in Overdue.
 */
export const TaskStatsSchema = z
    .object({
    /** The population these figures describe (BR-CRM-03-13). */
    scope: z.enum(['mine', 'all']),
    /** The header badges: pending tasks, and pending-and-past-due. */
    open: z.number().int(),
    overdue: z.number().int(),
    /**
     * §5's four urgency groups, counted. A group with no tasks is present
     * with zero — the SCREEN decides not to draw it (BR-CRM-03-12), and a
     * missing key would make "zero" and "not asked" the same thing.
     */
    buckets: z
        .object({
        overdue: z.number().int(),
        today: z.number().int(),
        week: z.number().int(),
        later: z.number().int(),
    })
        .strict(),
    /** The boundaries the counts used, echoed so the lists ask for the same. */
    window: z
        .object({
        /** Midnight tonight, on the company's clock — Today ends here. */
        todayEnd: z.string(),
        /** Seven days from that midnight — This week ends here. */
        weekEnd: z.string(),
        /** The instant "overdue" was decided against (BR-CRM-03-04). */
        now: z.string(),
    })
        .strict(),
})
    .strict();
/**
 * §5's month calendar, as ONE ROW PER DAY rather than as every task in the
 * month.
 *
 * The calendar cell draws at most three titles and then "+N more", so what it
 * needs from a day is a count and three titles — never the day's tasks. At
 * thirty-one rows it is bounded by the WINDOW rather than by the tenant,
 * which is the argument §5d makes for the report tables and the attendance
 * day view, and it is why this is an aggregate rather than another paged
 * list: a paged calendar would have to page each of thirty-one cells.
 */
export const TaskCalendarSchema = z
    .object({
    days: z
        .object({
        /** The local day, `YYYY-MM-DD`, bucketed on the COMPANY's clock. */
        day: z.string(),
        count: z.number().int(),
        /** The first three by due time — what the cell prints. */
        titles: z.string().array(),
    })
        .strict()
        .array(),
    scope: z.enum(['mine', 'all']),
})
    .strict();
/**
 * ── THE ACTIVITY REPORT (CRM-03 §7, BR-CRM-03-18) ──────────────────────────
 *
 * Under the Team list: two large numbers — calls completed and tasks
 * completed — and a bar per user. BR-CRM-03-18's two demands are properties
 * of this shape rather than screen discipline: the counts are against the
 * COMPLETION time (`completed_at`, never `due_at` — a call due in June and
 * made this week belongs to this week), and `byUser` DROPS users with
 * nothing completed by construction (only completers have rows to group; no
 * zero rows exist for the screen to forget to hide).
 *
 * THE WEEK IS SATURDAY TO FRIDAY, ON THE COMPANY'S CLOCK — and which weekday
 * starts it is CRM-03 §17 question 6's open question, deliberately isolated
 * in the service's one window function. The screen's own section decides the
 * interim reading: its worked week "11 to 17 July 2026" runs Saturday to
 * Friday, and question 6's proposed answer says the same. §13.1.5's
 * timesheets run Sun–Sat; if the owner rules the other way, the boundary is
 * one constant in `activityWeek`, nowhere else.
 */
export const TaskActivitySchema = z
    .object({
    /** The population counted (BR-CRM-03-13's labelling rule, same as list). */
    scope: z.enum(['mine', 'all']),
    /** The week counted: first day and last day inclusive, company clock. */
    weekStart: z.string(),
    weekEnd: z.string(),
    /** The two large numbers. */
    callsCompleted: z.number().int(),
    tasksCompleted: z.number().int(),
    /** One bar per user WITH completions — nobody appears at zero. */
    byUser: z
        .object({
        userId: z.string(),
        userName: z.string(),
        calls: z.number().int(),
        tasks: z.number().int(),
    })
        .strict()
        .array(),
})
    .strict();
const taskParams = z.object({ taskId: z.uuid() }).strict();
export const tasksContract = c.router({
    list: {
        method: 'GET',
        path: '/api/crm/tasks',
        query: pagedQuery({
            /** 'all' is honoured only for holders of crm.view; the response says. */
            scope: z.enum(['mine', 'all']).optional(),
            status: TaskStatusSchema.optional(),
            assigneeId: z.uuid().optional(),
            kind: TaskKindSchema.optional(),
            relatedKind: TaskRelatedKindSchema.optional(),
            relatedId: z.uuid().optional(),
            overdue: z.literal('true').optional(),
            /**
             * The due-date WINDOW, which is how §5's four urgency groups became
             * four reads instead of four `.filter()` calls. Inclusive of
             * `dueFrom`, exclusive of `dueTo`, so consecutive windows tile the
             * timeline without a task falling into two of them or none.
             */
            dueFrom: z.iso.datetime().optional(),
            dueTo: z.iso.datetime().optional(),
        }),
        responses: {
            200: pagedResponseWith('tasks', TaskSchema, {
                /** The population actually answered with — labels the Overdue badge. */
                scope: z.enum(['mine', 'all']),
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Tasks (rung-4 own floor; crm.view widens to everyone’s), PAGED (NFR-42) — every filter in SQL, the due window included. Overdue computed per row; the response names its scope (BR-CRM-03-13).',
    },
    stats: {
        method: 'GET',
        path: '/api/crm/tasks/stats',
        query: z.object({ scope: z.enum(['mine', 'all']).optional() }).strict(),
        responses: { 200: TaskStatsSchema, 401: MessageSchema, 403: MessageSchema },
        summary: '§5’s two badges and its four urgency-group counts — figures about EVERY task, so a page of the list above cannot narrow them. It also returns the window boundaries it used, so the group LISTS ask for exactly what it counted.',
    },
    activity: {
        method: 'GET',
        path: '/api/crm/tasks/activity',
        query: z.object({ scope: z.enum(['mine', 'all']).optional() }).strict(),
        responses: { 200: TaskActivitySchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'BR-CRM-03-18 — the weekly activity report under the Team list: calls and tasks completed THIS WEEK, counted against the completion time and never the due date, grouped per user with nothing-completed users dropped. Sat–Fri on the company’s clock (§17 question 6 holds the boundary).',
    },
    calendar: {
        method: 'GET',
        path: '/api/crm/tasks/calendar',
        query: z
            .object({
            scope: z.enum(['mine', 'all']).optional(),
            from: z.iso.datetime(),
            to: z.iso.datetime(),
        })
            .strict(),
        responses: { 200: TaskCalendarSchema, 401: MessageSchema, 403: MessageSchema },
        summary: '§5’s month grid as ONE ROW PER DAY — a count and the three titles a cell prints. Bounded by the WINDOW rather than by the tenant (§5d’s argument), which is why it is an aggregate and not a fifth paged list.',
    },
    create: {
        method: 'POST',
        path: '/api/crm/tasks',
        body: CreateTaskRequestSchema,
        responses: {
            201: TaskSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'New task: title + due date-time (BR-CRM-03-01); the SERVER fills priority/recurrence/reminder defaults (BR-CRM-03-02) and derives the related key from a typed name (BR-CRM-03-14). Assigning to somebody else needs crm.create.',
    },
    complete: {
        method: 'POST',
        path: '/api/crm/tasks/:taskId/complete',
        pathParams: taskParams,
        body: CompleteTaskRequestSchema.optional(),
        responses: {
            200: z
                .object({
                task: TaskSchema,
                /** BR-CRM-03-07: the ONE next copy of a repeating task, or null. */
                spawned: TaskSchema.nullable(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Complete (assignee, or crm.edit for another’s): stamps the time (BR-CRM-03-06), writes the optional outcome to notes, and a repeating task immediately spawns exactly ONE next copy — once per occurrence (BR-CRM-03-07/08, Q-CRM-03-3). Inert on cancelled (409).',
    },
    reopen: {
        method: 'POST',
        path: '/api/crm/tasks/:taskId/reopen',
        pathParams: taskParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: TaskSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reopen a completed task (the tick’s second state, BR-CRM-03-05): clears the completion time; the spawn marker stays so re-completing never double-spawns.',
    },
    cancel: {
        method: 'POST',
        path: '/api/crm/tasks/:taskId/cancel',
        pathParams: taskParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: TaskSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Cancel a pending task. It leaves the open count and the tick goes inert.',
    },
    followUp: {
        method: 'POST',
        path: '/api/crm/tasks/:taskId/follow-up',
        pathParams: taskParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: TaskSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Add follow-up (BR-CRM-03-10): a NEW pending task "Follow-up: <title>", due 7 days after the ORIGINAL due date, copying type/related/assignee/priority/reminder, recurrence forced to Does-not-repeat.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=tasks.js.map